import { Grid, Typography } from '@mui/material';
import { useTheme } from '@mui/styles';
import {
    ActionTypes,
    AdminRoleId,
    AgGridColumnDef,
    GridSettingsContext as IGridSettingsContext,
    SecondaryApiTypeEnum
} from '@tassat/common-definitions';
import { ClientUserForm } from '@tassat/forms';
import { ClientUser as ClientUserIcon } from '@tassat/icon';
import { UserRecord } from '@tassat/payments-definitions';
import React from 'react';
import { Route } from 'react-router-dom';

import { submitClientUser } from 'Containers/admin/forms/helpers/clientUserFormFn';
import { validateUsername } from 'Containers/admin/forms/helpers/commonHelpers';
import { hasApiCapabilities } from 'Lib/apiConnectionsHelper';
import { exportFirmMappedUserData } from 'Lib/exports/firmExports';
import { userNameSort } from 'Lib/stringHelpers';
import { ApplicationMuiTheme } from 'Lib/themes/muiTheme';
import { isSSOAuthenticationObj } from 'Lib/typeGuards';
import { useAppSelector } from 'Reducers/store/hooks';
import { getApiConnectionUsersWithConnectionArray } from 'Selectors/apiConnectionUsers';
import { getCurrentRole, getUsers } from 'Selectors/common';
import { getApiTypesByFirmId } from 'Selectors/firmApiTypes';
import { getAllUserApiTypesArray } from 'Selectors/userApiTypes';
import { getUsersByFirmId } from 'Selectors/users';
import { useFeatures } from 'SharedComponents/hooks/useFeatures';
import SideListComponent from 'SharedComponents/layouts/sideListComponent';
import { SidelistLayoutFormOptions } from 'SharedComponents/layouts/sideListLayout';
import { GridSettingsContext } from 'SharedComponents/layouts/sideListLayout/gridSettingsContext';
import { SideListGridOptions } from 'SharedComponents/sideList';

import FirmUserDetails from './firmUserDetails';
import {
    cellHeight, clientUserSortOptions, getClientUserFilterOptions, getFirmUsersColumnDefs
} from './gridOptions';

import { GridUserData, IFirmUsersProps } from './';

export function FirmUsers(props: IFirmUsersProps) {
    const {
        classes,
        firm,
        sortModel
    } = props;

    const userRole = useAppSelector(getCurrentRole);
    const usersList = useAppSelector(getUsers);
    const users = useAppSelector((state) => getUsersByFirmId(state, undefined, undefined, undefined, firm.FirmId)
        .sort(userNameSort));
    const firmSecondaryApiConnections = useAppSelector((state) => getApiTypesByFirmId(state, firm.FirmId, false));
    const allUsersSecondaryApiConnections = useAppSelector((state) => getAllUserApiTypesArray(state, false));
    const apiConnectionUsersWithConnection = useAppSelector(getApiConnectionUsersWithConnectionArray);
    const [clientFeatures] = useFeatures();
    const theme = useTheme<ApplicationMuiTheme>();

    const [ userFormOpen, setUserFormOpen ] = React.useState<boolean>(false);
    const [ editUser, setEditUser ] = React.useState<UserRecord | null>(null);
    const [ formActionType, setFormActionType ] =
    React.useState<ActionTypes>(clientFeatures?.isSSOEnabled ? ActionTypes.sso_add : ActionTypes.add);
    const [ deleteUser, setDeleteUser ] = React.useState(null);

    const editSelectedUser = (user, func) => {
        setDeleteUser(func);
        setEditUser(user);
        setFormActionType(ActionTypes.edit);
        setUserFormOpen(true);
    };

    const closeUserForm = () => {
        setDeleteUser(null);
        setEditUser(null);
        setFormActionType(clientFeatures?.isSSOEnabled ? ActionTypes.sso_add : ActionTypes.add);
        setUserFormOpen(false);
    };

    const isUserMappedToApi = React.useCallback(() => apiConnectionUsersWithConnection &&
        apiConnectionUsersWithConnection.some(
            (apiUser) => apiUser.apiConnectionUser.UserId === editUser?.UserId &&
                !apiUser.apiConnection?.APISecondaryConnectionType
        ), [ apiConnectionUsersWithConnection, editUser?.UserId ]);

    const gridUserList = React.useMemo((): Array<GridUserData> => {
        if (clientFeatures.fireblocks.api) {
            return users.map((user) => {
                return {
                    ...user,
                    HasApiConnections: hasApiCapabilities({
                        tokenApi: user.IsTokenAPIUser,
                        secondaryApiList: allUsersSecondaryApiConnections?.filter((api) => api.UserId === user.UserId)
                    })
                };
            });
        }

        return users;
    }, [ clientFeatures.fireblocks.api, users, allUsersSecondaryApiConnections ]);

    const layoutSettingsMemo: IGridSettingsContext = React.useMemo(() => {
        return {
            columnDefs: undefined,
            options: { cellHeight: 0 }
        };
    }, []);

    const firmUsersColumnDefs: AgGridColumnDef[] = React.useMemo(() => {
        return getFirmUsersColumnDefs(clientFeatures.isSSOEnabled);
    }, [clientFeatures.isSSOEnabled]);

    const gridOptions: SideListGridOptions = React.useMemo(() => ({
        idField: 'UserId',
        headerHeight: 60,
        cellHeight: cellHeight,
        filterOptions: getClientUserFilterOptions(theme),
        sortOptions: clientUserSortOptions,
        exportData: exportFirmMappedUserData,
        exportDefaultArgs: [ firm.FirmId, clientFeatures ],
        searchByColumn: 'UserName',
        sortModel
    }), [ firm.FirmId, theme, sortModel ]);

    const formOptions: SidelistLayoutFormOptions = React.useMemo(() => {
        const userSecondaryApiConnections =
            clientFeatures.fireblocks.api &&
            formActionType === ActionTypes.edit &&
            allUsersSecondaryApiConnections.filter((api) => api.UserId === editUser?.UserId);

        const isUserMappedToSecondaryApi = editUser &&
            apiConnectionUsersWithConnection?.some(
                (api) => api.apiConnectionUser.UserId === editUser.UserId &&
                    api.apiConnection?.APISecondaryConnectionType === SecondaryApiTypeEnum.FIREBLOCKS
            );

        return {
            formTitle: (userRole.AdminRoleId === AdminRoleId.BANK_OPS || userRole.AdminRoleId === AdminRoleId.CLIENT_SERVICES) ?
                `${formActionType === ActionTypes.add || formActionType === ActionTypes.sso_add ? 'Add New' : 'Edit'} User` : '',
            Form: (userRole.AdminRoleId === AdminRoleId.BANK_OPS || userRole.AdminRoleId === AdminRoleId.CLIENT_SERVICES) ?
                ClientUserForm : undefined,
            formModalWidth: '744px',
            showAddButton: !firm.IsClosed,
            formProps: {
                modalOpen: userFormOpen,
                onClose: closeUserForm,
                actionType: formActionType,
                editUser: editUser,
                deleteUser: deleteUser,
                firmId: firm?.FirmId,
                firm: firm,
                users: usersList,
                features: {
                    SSO: clientFeatures.authentication?.SSO,
                    SSOConfig: isSSOAuthenticationObj(clientFeatures.authentication),
                    loginType: clientFeatures.authentication.Type,
                    isSecondaryApiEnabled: clientFeatures.fireblocks.api
                },
                isUserMappedToApiConnection: editUser ? isUserMappedToApi() : false,
                submitClientUser: submitClientUser,
                validateUsernameAsync: validateUsername,
                userSecondaryApiConnections,
                firmSecondaryApiConnections,
                apiConnectionUsersWithConnection,
                isUserMappedToSecondaryApi
            }
        };
    }, [
        clientFeatures.fireblocks.api,
        clientFeatures.authentication,
        formActionType,
        allUsersSecondaryApiConnections,
        editUser,
        apiConnectionUsersWithConnection,
        userRole.AdminRoleId,
        firm,
        userFormOpen,
        closeUserForm,
        deleteUser,
        usersList,
        isUserMappedToApi,
        firmSecondaryApiConnections
    ]);

    const noUserSelectedMessage = React.useMemo(() => {
        return `Select a name on the left to view ${userRole.AdminRoleId !== AdminRoleId.BANK_OPS_SUPERVISOR && userRole.AdminRoleId !== AdminRoleId.PCG_ADMIN ? 'or edit ' : ''}a user's information, status, or account role.`;
    }, [userRole.AdminRoleId]);

    return (
        <GridSettingsContext.Provider value={layoutSettingsMemo}>
            <Grid
                className={classes.root}
                container
                direction='column'
                justifyContent='flex-start'
                spacing={0}
                wrap='nowrap'
            >
                <Grid
                    className={classes.usersContainer}
                    container
                    direction='row'
                    item
                    spacing={0}
                    wrap='nowrap'
                >
                    <Grid
                        className={classes.usersGridContainer}
                        item
                        xs={4}
                    >
                        <SideListComponent
                            columnDefs={firmUsersColumnDefs}
                            formOptions={formOptions}
                            gridOptions={gridOptions}
                            rowData={gridUserList}
                        />
                    </Grid>
                    <Grid
                        className={classes.selectedUserContainer}
                        item
                        xs={8}
                    >
                        <Route
                            exact
                            path='/admin/firms/:firmId/users/:userId'
                            render={props => (
                                <FirmUserDetails
                                    closeModal={() => setUserFormOpen(false)}
                                    editUser={editSelectedUser}
                                    firm={firm}
                                    {...props}
                                    isSSOEnabled={clientFeatures.isSSOEnabled}
                                />
                            )}
                        />
                        <Route
                            exact
                            path='/admin/firms/:firmId'
                            render={props => (
                                <Grid
                                    alignItems='center'
                                    className={classes.noSelectedUser}
                                    container
                                    direction='column'
                                    item
                                    spacing={0}
                                    wrap='nowrap'
                                >
                                    <ClientUserIcon
                                        className={classes.usersIcon}
                                        iconColor='#4A4C50'
                                    />
                                    <Typography variant='body1'>{noUserSelectedMessage}</Typography>
                                </Grid>
                            )}
                        />
                    </Grid>
                </Grid>
            </Grid>
        </GridSettingsContext.Provider>
    );
}